import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/StatusChip";
import ReviewSummaryBadge from "@/components/ReviewSummaryBadge";
import SafetyOnlyBadge from "@/components/SafetyOnlyBadge";
import MyRetakesCard from "@/components/MyRetakesCard";
import IncidentsSummaryCard from "@/components/IncidentsSummaryCard";
import GoodCatchCard from "@/components/GoodCatchCard";
import { useAuth } from "@/context/AuthContext";
import { Activity, AlertTriangle, CheckCircle2, ClipboardList, FileBox, FolderKanban, Plus, Search } from "lucide-react";

function StatCard({ label, value, icon: Icon, accent = "blue", testid }) {
  const accentMap = {
    blue: "text-blue-400 bg-blue-950/40 border-blue-900",
    red: "text-red-400 bg-red-950/40 border-red-900",
    amber: "text-amber-400 bg-amber-950/40 border-amber-900",
    emerald: "text-emerald-400 bg-emerald-950/40 border-emerald-900",
    slate: "text-slate-300 bg-slate-900 border-slate-800",
  };
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">{label}</div>
            <div className="font-display font-black text-4xl tracking-tight mt-2">{value}</div>
          </div>
          <div className={`p-2.5 rounded-md border ${accentMap[accent]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");

  const fetchAll = async () => {
    const [statsRes, projRes] = await Promise.all([
      api.get("/projects/stats"),
      api.get("/projects"),
    ]);
    setStats(statsRes.data);
    setProjects(projRes.data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return projects.slice(0, 8);
    const s = q.toLowerCase();
    return projects
      .filter(
        (p) =>
          (p.site_number || "").toLowerCase().includes(s) ||
          (p.site_name || "").toLowerCase().includes(s) ||
          (p.customer || "").toLowerCase().includes(s) ||
          (p.foreman || "").toLowerCase().includes(s) ||
          (p.project_name || "").toLowerCase().includes(s)
      )
      .slice(0, 12);
  }, [q, projects]);

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">Welcome back</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1" data-testid="dashboard-heading">
            {user?.full_name?.split(" ")[0] || "Field Team"}
          </h1>
        </div>
        <Button
          className="bg-red-700 hover:bg-red-600 font-display tracking-wide hidden sm:flex"
          onClick={() => navigate("/projects/new")}
          data-testid="dashboard-new-project"
        >
          <Plus className="w-4 h-4 mr-1.5" /> NEW PROJECT
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="dashboard-stats">
        <StatCard label="Active" value={stats?.active ?? "—"} icon={Activity} accent="blue" testid="stat-active" />
        <StatCard label="Drafts" value={stats?.drafts ?? "—"} icon={ClipboardList} accent="slate" testid="stat-drafts" />
        <StatCard label="Completed" value={stats?.completed ?? "—"} icon={CheckCircle2} accent="emerald" testid="stat-completed" />
        <StatCard label="Missing Docs" value={stats?.missing_docs ?? "—"} icon={AlertTriangle} accent="red" testid="stat-missing" />
      </div>

      {/* Silent-when-empty. Renders itself null on 0 retakes so it
          only surfaces for users who actually have work to do. */}
      <MyRetakesCard />

      {/* Silent-when-clean: renders null when there are zero open
          incidents AND no logged injury history. Otherwise surfaces
          "N Open Incidents" (color-coded by severity) + "Days Since
          Last Injury" (industry-standard KPI). */}
      <IncidentsSummaryCard />

      {/* Positive reinforcement counterpart to the incidents summary.
          Shows nothing when there's no near-miss in the last 30 days
          so it never becomes background noise. */}
      <GoodCatchCard />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
        <StatCard label="Pending Safety" value={stats?.pending_safety ?? "—"} icon={ClipboardList} accent="amber" testid="stat-pending-safety" />
        <StatCard label="Pending Closeout" value={stats?.pending_closeout ?? "—"} icon={FileBox} accent="amber" testid="stat-pending-closeout" />
        <StatCard label="Total Projects" value={stats?.total ?? "—"} icon={FolderKanban} accent="blue" testid="stat-total" />
      </div>

      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-display font-bold text-xl tracking-tight">Recent Projects</h2>
        <div className="relative w-full max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Search site, customer, foreman..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-slate-900 border-slate-700"
            data-testid="dashboard-search"
          />
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0 divide-y divide-slate-800">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400" data-testid="dashboard-no-projects">
              No projects yet. <button onClick={() => navigate("/projects/new")} className="text-blue-400 underline">Create the first one</button>.
            </div>
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="w-full text-left p-4 hover:bg-slate-800/50 transition-colors flex items-center gap-4"
                data-testid={`project-row-${p.id}`}
              >
                <div className="font-mono text-xs text-slate-400 w-24 truncate">{p.site_number}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-50 truncate flex items-center gap-2 flex-wrap">
                    <span className="truncate">{p.site_name || p.project_name}</span>
                    {/* Pill only when BOTH names exist AND differ,
                        otherwise the row would show the same label
                        twice (title falls back to project_name when
                        site_name is blank). */}
                    {p.site_name && p.project_name && p.project_name !== p.site_name && (
                      <span
                        className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-950/60 border border-blue-800 text-blue-200 max-w-[16rem] truncate"
                        title={p.project_name}
                        data-testid={`dashboard-project-name-${p.id}`}
                      >
                        {p.project_name}
                      </span>
                    )}
                    <SafetyOnlyBadge projectType={p.project_type} size="xs" />
                    <ReviewSummaryBadge summary={p.review_summary} />
                  </div>
                  <div className="text-xs text-slate-400 truncate">{p.customer} · {p.foreman || "—"}</div>
                </div>
                <StatusChip status={p.status} />
              </button>
            ))
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
